// Barcode reading from the camera.
//
// Android Chrome (and desktop Chrome) have a native BarcodeDetector; Safari on iPhone /
// iPad doesn't, so there the `barcode-detector` ponyfill (ZXing compiled to WebAssembly) is
// used. The .wasm is served from this app (public/vendor) rather than the library's CDN,
// so it works under the app's Content-Security-Policy (connect-src 'self').

// Product labels are EAN-13 / UPC; some parts use their part number as a Code 128 barcode.
export const BARCODE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'code_93', 'itf', 'qr_code', 'data_matrix'];

export const cameraSupported = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function';

let detectorPromise = null;

const nativeDetector = async () => {
  if (typeof window === 'undefined' || !('BarcodeDetector' in window)) return null;
  try {
    const supported = await window.BarcodeDetector.getSupportedFormats();
    const formats = BARCODE_FORMATS.filter((f) => supported.includes(f));
    if (!formats.includes('ean_13') || !formats.includes('code_128')) return null;
    return new window.BarcodeDetector({ formats });
  } catch {
    return null;
  }
};

export const getBarcodeDetector = () => {
  if (!detectorPromise) {
    detectorPromise = (async () => {
      const native = await nativeDetector();
      if (native) return native;
      const zxing = await import('barcode-detector/ponyfill');
      zxing.setZXingModuleOverrides({
        locateFile: (path, prefix) => (path.endsWith('.wasm')
          ? `${process.env.PUBLIC_URL || ''}/vendor/zxing_reader-${zxing.ZXING_WASM_VERSION}.wasm`
          : prefix + path),
      });
      return new zxing.BarcodeDetector({ formats: BARCODE_FORMATS });
    })().catch((error) => {
      detectorPromise = null; // let the next attempt try again
      throw error;
    });
  }
  return detectorPromise;
};

// What to tell the operator when the camera can't be opened
export const cameraErrorMessage = (error) => {
  const name = error && error.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Camera access is blocked. Allow the camera for this site: on iPhone/iPad tap "aA" in the address bar, Website Settings, Camera, Allow (or Settings, Safari, Camera). On Android tap the lock icon, Permissions, Camera.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'No camera found on this device.';
  if (name === 'NotReadableError' || name === 'AbortError') return 'The camera is in use by another app. Close it and try again.';
  return 'Could not start the camera. Try again, or type the barcode instead.';
};
